import { Fragment } from "react";

import Divider from "@mui/material/Divider";

import ErrorState from "@app/analytics/components/ErrorState";
import { QuestionsAskedGradientRange } from "@app/analytics/components/GradientRange/CoachingGradientRange";
import TableCell from "@app/analytics/components/Table/TableCell";
import { getQuestionsAskedDescription } from "@app/analytics/utils/coachingUtils";
import { HeadCell } from "@app/common/hooks/useDataTable";
import { CoachingSession } from "@app/common/types";
import { getQuestionsAskedSemanticScore } from "@app/common/utils/coachingUtils";

import { Column, Row } from "@rui/Stack";

import CoachingTable from "./components/CoachingTable";
import useCoachingDashboardData from "./hooks/useCoachingDashboardData";
import {
  Card,
  EmptyCardText,
  LoadingSkeleton,
  SectionDescription,
  SectionHeading,
  SectionOverviewContainer,
  SectionSubHeading,
  ValueWithSemanticIcon,
} from "./styles";

const QUESTIONS_ASKED_HEAD_CELLS: HeadCell<CoachingSession>[] = [
  {
    id: "count",
    label: "# of Questions",
  },
];

const QuestionsAsked = () => {
  const { isError, isInitialLoading, questions, refetch } =
    useCoachingDashboardData();
  const questionsPerMeeting = questions.average;
  const descriptionSegments = getQuestionsAskedDescription(questionsPerMeeting);

  if (isError) {
    return <ErrorState refetch={refetch} />;
  }

  return (
    <>
      <Column spacing={1}>
        <Row variant="apart">
          <SectionHeading>Questions asked</SectionHeading>
          {questionsPerMeeting.value != null && (
            <ValueWithSemanticIcon
              score={questions.semanticScore}
              variant="h3"
              sx={{ fontWeight: 500 }}
            >
              {questionsPerMeeting.formattedValue} per meeting
            </ValueWithSemanticIcon>
          )}
        </Row>
        <SectionDescription>
          {descriptionSegments.map((segment) =>
            segment.style ? (
              <span key={segment.value} style={segment.style}>
                {segment.value}
              </span>
            ) : (
              <Fragment key={segment.value}>{segment.value}</Fragment>
            )
          )}
        </SectionDescription>
        <SectionOverviewContainer>
          <Card title="Range" subtitle="Avg questions / meeting">
            {isInitialLoading ? (
              <LoadingSkeleton width="100%" />
            ) : questionsPerMeeting.value == null ? (
              <EmptyCardText />
            ) : (
              <QuestionsAskedGradientRange
                count={questionsPerMeeting.rawValue}
              />
            )}
          </Card>
        </SectionOverviewContainer>
      </Column>
      <Divider />
      <Column spacing={1}>
        <SectionSubHeading>Meetings</SectionSubHeading>
        <SectionDescription>
          Review meeting reports where you asked questions.
        </SectionDescription>
        <CoachingTable
          defaultOrderBy="count"
          defaultOrder="asc"
          isLoading={isInitialLoading}
          renderSectionCells={(session) => (
            <TableCell>
              <ValueWithSemanticIcon
                score={getQuestionsAskedSemanticScore(session.count)}
                variant="bodySecondaryReg"
                sx={{ minWidth: "30px", textAlign: "right" }}
              >
                {session.count}
              </ValueWithSemanticIcon>
            </TableCell>
          )}
          rows={questions.sessions}
          sectionHeadCells={QUESTIONS_ASKED_HEAD_CELLS}
        />
      </Column>
    </>
  );
};

export default QuestionsAsked;
